import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getAssetSystems,
  createAssetSystem,
  updateAssetSystem,
  deleteAssetSystem,
} from '../api/asset-systemAPI';

const AssetSystemListLayer = () => {
  const userProfile = useUserProfile();
  const [assetSystems, setAssetSystems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '' });
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchAssetSystems = async () => {
    try {
      const response = await getAssetSystems();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setAssetSystems(list);
    } catch (error) {
      console.error('Error fetching asset systems:', error);
    }
  };

  useEffect(() => {
    fetchAssetSystems();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }

    if (assetSystems.length > 0 && tableRef.current) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [assetSystems]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ code: '', name: '' });
    setErrors({});
  };

  const handleAdd = () => {
    setFormData({ code: '', name: '' });
    setEditingId(null);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setFormData({ code: item?.code ?? '', name: item?.name ?? '' });
    setEditingId(item?.id ?? null);
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset system?')) {
      return;
    }

    try {
      await deleteAssetSystem(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchAssetSystems();
    } catch (error) {
      console.error('Error deleting asset system:', error);
    }
  };

  const handleSave = async () => {
    const trimmedCode = formData.code?.toString().trim();
    const trimmedName = formData.name?.toString().trim();

    const validationErrors = {};

    if (!trimmedCode) {
      validationErrors.code = 'Code is required';
    }

    if (!trimmedName) {
      validationErrors.name = 'Name is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = { code: trimmedCode, name: trimmedName };

    try {
      if (editingId) {
        await updateAssetSystem(editingId, payload);
      } else {
        await createAssetSystem(payload);
      }

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      await fetchAssetSystems();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving asset system:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {userProfile?.privileges?.includes('asset-system:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={handleAdd}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add System
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Code</th>
                <th>Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assetSystems.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4 rounded-0">No Systems Setup</td>
                </tr>
              )}
              {assetSystems.map((item) => (
                <tr key={item.id}>
                  <td style={{ textAlign: 'left' }}><strong>{item.code}</strong></td>
                  <td><strong>{item.name}</strong></td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('tasks:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('tasks:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Asset System' : 'New Asset System'}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Code *</label>
                  <input
                    name="code"
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    value={formData.code}
                    onChange={handleInputChange}
                  />
                  {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    name="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetSystemListLayer;
